import {
  FilterTabEnum,
  JobItem,
  JobStatusFilterSeparateTabEnum,
  RefreshQuickBooksTokenResponse,
} from 'domains/workspaceDomain';
import { JobWorkflowStatusEnum } from 'domains/workspaceDomain';
import { RevxButton } from 'components/RevxButton';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Space, Button, Tooltip } from 'antd';
import { PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { RevxShippingInfo } from 'components/RevxShippingInfo';
import CardActionViewJobAdjustmentBtn from './CardActionViewJobAdjustmentBtn';
import { JobAdjustmentStatusEnum } from 'domains/workspaceDomain';
import { useRouteMatchFilter } from '../../hooks/useRouteMatchFilter';
import { WORKSPACE_DELAY_TIME } from 'utils/constants';
// import CardActionShippedInfo from './CardActionShippedInfo';
import CardActionEnterShippingInfoBtn from './CardActionEnterShippingInfoBtn';
import moment from 'moment';
import { RevxDate, SHIPPING_DATE_FORMAT } from 'components/RevxDate';
import { InvoiceStatusEnum } from 'domains/workspaceDomain';
import { RevxMoney } from 'components/RevxMoney';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import workspaceService from 'services/jobService';
import { sleep } from 'utils/misc';
import { useJobStatusCollection } from 'hooks';
import CardActionUploadInvoice from './CardActionUploadInvoice';
import { Text, TextHeavy } from 'components/RevxTypography';
import CardActionArchiveBtn from './CardActionArchiveBtn';
import CardCancelJobBtn from './CardCancelJobBtn';
import CardBuyerCancelJobBtn from './CardBuyerCancelJobBtn';
import CardActionUploadInvoiceTips from './CardActionUploadInvoiceTips';
import useJobCardInfo from '../../hooks/useJobCardInfo';
import { ShareInvoiceDropdown } from 'features/Workspace/components/ShareInvoiceDropdown';
import NameBadge from 'features/Workspace/components/NameBadge';
import './CardActions.less';
import { InvoiceFileBlock } from 'components/InvoiceFileBlock';
import { toast } from 'components/RevxMessage';

const { FILES, PROOFS, SUMMARY, SHIPPING_BILLING } = FilterTabEnum;
const { AWAITING_ACCEPT, AWAITING_APPROVAL } = JobAdjustmentStatusEnum;
const { APPROVED, MARK_AS_PAID, APPLIED } = InvoiceStatusEnum;

const {
  PO_SENT,
  PO_ACCEPT,
  PO_READ,
  PREPARE_PROOF,
  PROOF_AWAIT_APPROVAL,
  PROOF_NEED_REVISION,
  RFM_NEEDED,
  RFM,
  RFQ,
  PO_DECLINED,
  IN_PRODUCTION,
  SHIPPED,
  DELIVERED,
  INVOICING,
  COMPLETED,
} = JobWorkflowStatusEnum;

const { PRE_CONFIGURED_SKU_ARCHIVED_INVOICES, PRE_CONFIGURED_SKU } =
  JobStatusFilterSeparateTabEnum;

type CardActionsProps = {
  data: JobItem;
  scrollLoading: boolean;
  setActiveTab: (tab: FilterTabEnum) => void;
  scrollDetailTab: (e: React.MouseEvent<HTMLElement>) => void;
};

const CardActions = ({
  data,
  scrollLoading,
  setActiveTab,
  scrollDetailTab,
}: CardActionsProps) => {
  const routerParam = useRouteMatchFilter();
  const { t } = useTranslation();
  const { invoicingTabsKey, jobList, lastUpdatedJobs } = useAppSelector(
    (state) => state.workspace
  );
  const statusFilters = useJobStatusCollection();
  const dispatch = useAppDispatch();

  // 从 Redux store 中获取最新的 job 数据
  // 优先使用 jobList 中的数据，如果找不到（可能因为分页），则检查 lastUpdatedJobs
  const jobFromList = React.useMemo(
    () =>
      jobList.find((job) =>
        job.isQuote ? job.quoteId === data.quoteId : job.id === data.id
      ),
    [jobList, data.id, data.quoteId]
  );

  // 从 lastUpdatedJobs 数组中查找当前 job 的更新
  const lastUpdateForThisJob = React.useMemo(
    () =>
      lastUpdatedJobs.find((job) =>
        job.isQuote ? job.quoteId === data.quoteId : job.id === data.id
      ),
    [lastUpdatedJobs, data.id, data.quoteId]
  );

  // 合并数据：jobList 中的数据 > lastUpdatedJobs 中的更新 > 原始 prop
  const currentJob = React.useMemo(
    () =>
      jobFromList ||
      (lastUpdateForThisJob ? { ...data, ...lastUpdateForThisJob } : data),
    [jobFromList, lastUpdateForThisJob, data]
  );

  const {
    workflowStatus,
    adjustmentStatus,
    id,
    jobCardShippingItems,
    invoiceStatus,
    expectPaymentBy,
    invoice,
    supplierPrintingCharge,
    supplierFreightCharge,
    supplierCharge,
    artworkConfirm,
    sendInvoiceFileUrl,
    sendQuickBooksInvoiceFileUrl,
    receiveInvoiceFileUrl,
    receiveQuickBooksInvoiceFileUrl,
    invoiceExpired,
    quickBooksInvoiceExpired,
    invoiceNumber,
    batchUploadInvoiceNumber,
  } = currentJob;
  const {
    hasProducer,
    hasProxy,
    hasOperator,
    hasCustomer,
    isInternalJob,
    onlyOneParticipant,
  } = useJobCardInfo(currentJob);

  const showViewJobAdjustment = [AWAITING_ACCEPT, AWAITING_APPROVAL].includes(
    adjustmentStatus
  );

  const showUploadAction = hasCustomer || hasProxy;

  const isPreConfigTab = [
    PRE_CONFIGURED_SKU,
    PRE_CONFIGURED_SKU_ARCHIVED_INVOICES,
  ].includes(invoicingTabsKey);

  const requestPaymentHandler = async () => {
    await dispatch.app.showLoading();
    await workspaceService.sendInvoiceToRevshop(id);
    await sleep(WORKSPACE_DELAY_TIME);
    await sendSuccess();
  };

  const generateInvoiceHandler = async () => {
    await dispatch.app.showLoading();
    const refreshQuickBooksTokenResponse =
      (await workspaceService.refreshQuickBooksToken()) as RefreshQuickBooksTokenResponse;
    if (refreshQuickBooksTokenResponse.hasLinkQuickBooks) {
      if (
        refreshQuickBooksTokenResponse.tokenExpired &&
        refreshQuickBooksTokenResponse.loginUrl
      ) {
        await dispatch.app.hideLoading();
        window.location.href = refreshQuickBooksTokenResponse.loginUrl;
        return;
      }
    }

    try {
      // 调用生成 invoice 的 API，它会返回更新后的 job 信息
      const generateInvoiceResult =
        await workspaceService.generateInvoiceToRevshop(id);
      console.log('Generated invoice data:', generateInvoiceResult);

      // 直接更新当前 job 的数据，而不是刷新整个列表
      if (generateInvoiceResult) {
        const updatePayload: Partial<JobItem> = {
          id: id,
        };

        // 添加返回的文件 URL
        if (
          generateInvoiceResult.invoiceType === 2 &&
          generateInvoiceResult.path
        ) {
          updatePayload.sendInvoiceFileUrl = generateInvoiceResult.path;
          updatePayload.invoiceExpired = false; // 重新生成后设为未过期
        }
        if (
          generateInvoiceResult.invoiceType === 1 &&
          generateInvoiceResult.path
        ) {
          updatePayload.sendQuickBooksInvoiceFileUrl =
            generateInvoiceResult.path;
          updatePayload.quickBooksInvoiceExpired = false; // 重新生成后设为未过期
          updatePayload.invoiceNumber = generateInvoiceResult.invoiceNumber;
        }
        console.log('Updating job item with payload:', updatePayload);
        // 使用 updateJobItem reducer 直接更新 state 中的 jobList
        dispatch.workspace.updateJobItem(updatePayload);
      }

      // 更新通知和计数
      await dispatch.alert.getNotificationAndAlerts();
      dispatch.workspace.getJobCountByStatusFilter(statusFilters);
      toast(t('workspace.list.generateInvoiceSuccess'));
    } catch (error) {
      console.error('Failed to generate invoice:', error);
      // 如果出错，回退到刷新整个列表
      await sendSuccess();
    } finally {
      await dispatch.app.hideLoading();
    }
  };

  const sendSuccess = async () => {
    console.log('sendSuccess - Starting data refresh');
    console.log('statusFilters:', statusFilters);
    console.log('invoicingTabsKey:', invoicingTabsKey);
    console.log('isPreConfigTab:', isPreConfigTab);

    try {
      await dispatch.alert.getNotificationAndAlerts();
      console.log('sendSuccess - Alert notifications updated');

      if (isPreConfigTab) {
        console.log('sendSuccess - Reloading group job list');
        await dispatch.workspace.reloadGroupJobList();
      } else {
        console.log('sendSuccess - Force reloading job list');
        // 强制刷新：先清空列表再重新加载
        dispatch.workspace.setState({
          jobList: [],
          page: 1,
        });
        console.log('sendSuccess - Job list state cleared');

        // 调用重置函数
        dispatch.workspace.reloadJobList();
        console.log('sendSuccess - reloadJobList called');

        // 给一点时间让列表重新加载
        await new Promise((resolve) => setTimeout(resolve, 500));
        console.log('sendSuccess - Wait completed');
      }

      console.log('sendSuccess - Updating job counts');
      dispatch.workspace.getJobCountByStatusFilter(statusFilters);
      console.log('sendSuccess - Job counts updated');

      console.log('sendSuccess - Data refresh completed');
    } catch (error) {
      console.error('sendSuccess - Error during refresh:', error);
    } finally {
      console.log('sendSuccess - Hiding loading state');
      await dispatch.app.hideLoading();
      console.log('sendSuccess - Loading state hidden, function completed');
    }
  };

  // 处理 share invoice 后的更新，只更新当前 job 而不刷新整个列表
  const handleShareInvoiceSuccess = async (sharedUser?: {
    firstName: string;
    lastName: string;
    email: string;
  }) => {
    if (!sharedUser) {
      console.log('No shared user info provided, skipping update');
      return;
    }

    console.log(
      'Share invoice success, updating job with new share user:',
      sharedUser
    );

    // 获取当前的 shareList
    const currentShareList = currentJob.shareList || [];

    // 检查是否已经存在相同邮箱的用户
    const existingUsers = currentShareList.filter(
      (user) => user.email?.toLowerCase() === sharedUser.email?.toLowerCase()
    );

    let updatedShareList;

    if (existingUsers.length > 0) {
      // 如果用户已存在（可能有多个），更新所有匹配的用户信息并将 expired 设为 false
      console.log(
        `Found ${existingUsers.length} existing users with email ${sharedUser.email}, updating expired status`
      );

      updatedShareList = currentShareList.map((user) => {
        if (user.email?.toLowerCase() === sharedUser.email?.toLowerCase()) {
          return {
            ...user,
            firstName: sharedUser.firstName,
            lastName: sharedUser.lastName,
            email: sharedUser.email,
            expired: false, // 重新分享后将过期状态设为 false
          };
        }
        return user;
      });
    } else {
      // 添加新的共享用户到列表
      updatedShareList = [
        ...currentShareList,
        {
          firstName: sharedUser.firstName,
          lastName: sharedUser.lastName,
          email: sharedUser.email,
          expired: false,
        },
      ];
    }

    console.log('Updating shareList, total users:', updatedShareList.length);

    // 使用 updateJobItem 更新当前 job 的 shareList
    dispatch.workspace.updateJobItem({
      id: id,
      shareList: updatedShareList,
    });

    // 更新通知和计数
    await dispatch.alert.getNotificationAndAlerts();
  };

  const commonBtnNode = (
    disabled: boolean,
    text: string | ReactNode,
    tabType?: FilterTabEnum
  ) => {
    return (
      <RevxButton
        type="primary"
        size="large"
        loading={scrollLoading}
        disabled={disabled}
        onClick={(e) => {
          if (tabType) {
            setActiveTab(tabType);
            scrollDetailTab(e);
          }
        }}
      >
        {text}
      </RevxButton>
    );
  };

  const attachNode = () => {
    if (hasProducer && onlyOneParticipant && workflowStatus !== PO_ACCEPT) {
      return;
    }
    if (artworkConfirm) {
      if (onlyOneParticipant && (hasProxy || hasCustomer)) {
        return <h3 className="bold">{t('workspace.list.comingSoonProof')}</h3>;
      }
      return (
        <Space direction="vertical">
          <div>
            <TextHeavy>{t('workspace.list.supplierName')}:</TextHeavy>{' '}
            <Text>{currentJob.supplierOrgName}</Text>
          </div>
          <div>
            <TextHeavy>{t('workspace.list.dateSent')}:</TextHeavy>{' '}
            <Text>
              <RevxDate
                dateStr={currentJob?.jobPo?.sentTime}
                formatStr={SHIPPING_DATE_FORMAT}
              />
            </Text>
          </div>
          <div>
            <RevxShippingInfo
              shippingItems={jobCardShippingItems}
              displayItems={[
                {
                  label: t('workspace.list.reqShipDate'),
                  valueProp: 'estimatedShipTime',
                },
              ]}
            />
          </div>
        </Space>
      );
    }
    if (!showUploadAction) {
      return <h3 className="bold">{t('workspace.list.awaitArt')}</h3>;
    }
    return commonBtnNode(
      !showUploadAction,
      t('workspace.list.attachArt'),
      FILES
    );
  };
  const proofNode = () => {
    let onlyText = false;
    const textMap = () => {
      if (hasProducer) {
        switch (workflowStatus) {
          case PREPARE_PROOF:
            if (!hasProducer) {
              onlyText = true;
              return t('workspace.list.comingSoonProof');
            }
            return t('workspace.list.attachProof');
          case PROOF_AWAIT_APPROVAL:
            if (onlyOneParticipant) {
              onlyText = true;
              return t('workspace.list.awaitingApproval');
            }
            if (isInternalJob) {
              return t('workspace.list.approvalProof');
            }
            if (!hasCustomer) {
              onlyText = true;
              return t('workspace.list.awaitingApproval');
            } else {
              return t('workspace.list.attachProof');
            }
          case PROOF_NEED_REVISION:
            return t('workspace.list.attachProof');
          default:
            return '';
        }
      } else {
        switch (workflowStatus) {
          case PREPARE_PROOF:
          case PO_SENT:
          case PO_READ:
          case RFM_NEEDED:
          case RFM:
          case RFQ:
          case PO_DECLINED:
          case PO_ACCEPT:
            onlyText = true;
            if (PREPARE_PROOF === workflowStatus) {
              return t('workspace.list.comingSoonProof');
            }
            return t('workspace.list.awaitingProofing');
          case PROOF_AWAIT_APPROVAL:
            return t('workspace.list.approvalProof');
          case PROOF_NEED_REVISION:
            onlyText = true;
            return t('workspace.list.awaitProof');
        }
      }
    };
    /*const proofDisabled = () => {
      if (hasOperator || hasCustomer || hasProxy) {
        return workflowStatus !== PROOF_AWAIT_APPROVAL;
      } else if (hasProducer) {
        return workflowStatus === PROOF_AWAIT_APPROVAL && !hasCustomer;
      }
      return false;
    };*/
    const textValue = textMap();
    if (onlyText) return <h3 className="bold">{textValue}</h3>;
    return commonBtnNode(false, textMap(), PROOFS);
  };

  const poNode = () => {
    if (
      hasProducer &&
      [PO_SENT, PO_READ].includes(workflowStatus) &&
      !isInternalJob
    ) {
      return (
        <Space direction="vertical">
          <div style={{ textAlign: 'center' }}>
            {commonBtnNode(false, t('workspace.list.respondToPO'), SUMMARY)}
          </div>
        </Space>
      );
    }
  };
  const customerShippingInfo = (
    <RevxShippingInfo
      shippingItems={jobCardShippingItems}
      displayItems={[
        {
          label: t('workspace.list.dateShipped'),
          valueProp: 'actualShipTime',
        },
        {
          label: t('workspace.list.shippingCarrier'),
          valueProp: 'carrierType',
        },
        {
          label: t('workspace.list.trackingNumber'),
          valueProp: 'trackingCode',
        },
      ]}
    />
  );
  const viewJobAdjustmentBtn = (
    <div>
      <CardActionViewJobAdjustmentBtn
        data={currentJob}
        filter={routerParam.defaultFilter}
        onModalSuccess={sendSuccess}
      />
    </div>
  );
  const inProductionNode = () => {
    if (hasProducer) {
      return (
        <CardActionEnterShippingInfoBtn
          data={currentJob}
          scrollDetailTab={scrollDetailTab}
          scrollLoading={scrollLoading}
          setActiveTab={setActiveTab}
        />
      );
    }
  };

  const jobChargesBlock = (
    <div className="job-charges-wrap">
      <h3>Job Charges</h3>
      <p>
        Printing: <RevxMoney value={supplierPrintingCharge} />
      </p>
      <p>
        Freight: <RevxMoney value={supplierFreightCharge} />
      </p>
      <div>
        Invoice Total: <RevxMoney value={supplierCharge} />
      </div>
    </div>
  );

  const showInvoiceFile = () => {
    if (isInternalJob) {
      return true;
    }
    return hasProxy || hasOperator || hasCustomer;
  };

  const showInvoiceButton = () => {
    if (isInternalJob) {
      return hasOperator || hasProducer;
    }
    return hasOperator;
  };

  const sendInvoiceFileBlock = (
    <InvoiceFileBlock
      fileUrl={sendInvoiceFileUrl}
      title="Revshop Invoice"
      jobId={id}
      type="send"
      show={showInvoiceFile() && !!sendInvoiceFileUrl}
      expired={invoiceExpired}
    />
  );

  const sendQuickBooksInvoiceFileBlock = (
    <InvoiceFileBlock
      fileUrl={sendQuickBooksInvoiceFileUrl}
      title="QuickBooks Invoice"
      jobId={id}
      type="sendQuickBooks"
      show={showInvoiceFile() && !!sendQuickBooksInvoiceFileUrl}
      expired={quickBooksInvoiceExpired}
      invoiceNumber={invoiceNumber}
    />
  );

  const batchUploadInvoiceBlock = batchUploadInvoiceNumber ? (
    <div style={{ textAlign: 'center', fontSize: '12px', color: '#666', marginTop: '2px' }}>
      Batch Invoice Number: {batchUploadInvoiceNumber}
    </div>
  ) : null;

  const receiveInvoiceFileBlock = (
    <InvoiceFileBlock
      fileUrl={receiveInvoiceFileUrl}
      title="Receive Job Invoice"
      jobId={id}
      type="receive"
      show={showInvoiceFile() && !!receiveInvoiceFileUrl}
    />
  );

  const receiveQuickBooksInvoiceFileBlock = (
    <InvoiceFileBlock
      fileUrl={receiveQuickBooksInvoiceFileUrl}
      title="Receive QuickBooks Invoice"
      jobId={id}
      type="receiveQuickBooks"
      show={showInvoiceFile() && !!receiveQuickBooksInvoiceFileUrl}
    />
  );

  const completeBtnBlock = (
    <>
      {!isPreConfigTab && (
        <RevxFlexLayout justify="justify-center">
          <RevxButton
            type="primary"
            size="large"
            onClick={async (e) => {
              await requestPaymentHandler();
            }}
          >
            {t('workspace.list.complete')}
          </RevxButton>
        </RevxFlexLayout>
      )}
    </>
  );
  const generateInvoiceBtnBlock = showInvoiceButton() && (
    <>
      {
        <RevxFlexLayout justify="justify-center">
          <RevxButton
            type="primary"
            size="large"
            onClick={async (e) => {
              await generateInvoiceHandler();
            }}
          >
            {sendInvoiceFileUrl || sendQuickBooksInvoiceFileUrl
              ? t('workspace.list.reGenerateInvoice')
              : t('workspace.list.generateInvoice')}
          </RevxButton>
        </RevxFlexLayout>
      }
    </>
  );
  const shippingNode = () => {
    if (hasOperator) {
      return (
        <Space direction="vertical">
          <div>{customerShippingInfo}</div>
        </Space>
      );
    } else if (hasProducer) {
      if (isInternalJob) {
        return (
          <>
            {jobChargesBlock}
            {completeBtnBlock}
          </>
        );
      }
      return (
        <Space direction="vertical">
          <div>
            {!showViewJobAdjustment && (
              <>
                <CardActionUploadInvoice data={currentJob} />
                <CardActionUploadInvoiceTips
                  data={currentJob}
                  onModalSuccess={sendSuccess}
                />
              </>
            )}
          </div>
        </Space>
      );
    } else {
      return (
        <Space direction="vertical">
          <div>{customerShippingInfo}</div>
        </Space>
      );
    }
  };
  const paymentProgressBlock = (showComplete = true) => {
    return (
      <Space direction="vertical">
        <div style={{ textAlign: 'center' }}>
          {invoiceStatus === APPLIED && expectPaymentBy && (
            <div>
              <h3 className="bold">
                {t('workspace.jobStatus.paymentProgress')}
              </h3>
              <div>
                <span style={{ marginRight: 5 }}>
                  {t('workspace.jobStatus.expectPaymentBy', {
                    time: moment(expectPaymentBy).format(SHIPPING_DATE_FORMAT),
                  })}
                </span>
              </div>
            </div>
          )}
          {[APPROVED, MARK_AS_PAID, APPLIED].includes(invoiceStatus) &&
            invoice.markAsPaidTime && (
              <div>
                <h3 className="bold">Payment sent on</h3>
                <div>
                  <span style={{ marginRight: 5 }}>
                    {moment(invoice.markAsPaidTime).format(
                      SHIPPING_DATE_FORMAT
                    )}
                  </span>
                </div>
              </div>
            )}
          {showComplete && (
            <>
              {[COMPLETED, DELIVERED, INVOICING].includes(workflowStatus) && (
                <h3 style={{ fontWeight: 'bold' }}>
                  {t('workspace.list.jobCompleted')}
                </h3>
              )}
            </>
          )}
        </div>
        {generateInvoiceBtnBlock}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1px',
            flexWrap: 'wrap', // 在小屏幕上允许换行
          }}
        >
          {sendInvoiceFileBlock}
          {sendQuickBooksInvoiceFileBlock}
        </div>
        {batchUploadInvoiceBlock}
        {shareInvoiceBlock}
      </Space>
    );
  };
  const deliverNode = () => {
    if ((onlyOneParticipant && (hasCustomer || hasProxy)) || hasOperator) {
      return customerShippingInfo;
    }
    if (hasOperator) {
      return paymentProgressBlock();
    } else if (hasProducer) {
      if (isInternalJob) {
        return (
          !showViewJobAdjustment && (
            <Space direction="vertical">
              {jobChargesBlock}
              {completeBtnBlock}
            </Space>
          )
        );
      } else {
        if (!showViewJobAdjustment) {
          return (
            <>
              <CardActionUploadInvoice data={currentJob} />
              <CardActionUploadInvoiceTips
                data={currentJob}
                onModalSuccess={sendSuccess}
              />
            </>
          );
        }
      }
    } else {
      return paymentProgressBlock();
    }
  };

  const invoiceNode = () => {
    if (hasOperator) {
      return paymentProgressBlock(false);
    } else if (hasProducer) {
      if (isInternalJob && !isPreConfigTab) {
        return (
          <Space direction="vertical">
            {commonBtnNode(
              false,
              t('workspace.list.markAsPaid'),
              SHIPPING_BILLING
            )}
            {generateInvoiceBtnBlock}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '1px',
                flexWrap: 'wrap', // 在小屏幕上允许换行
              }}
            >
              {sendInvoiceFileBlock}
              {sendQuickBooksInvoiceFileBlock}
            </div>
            {batchUploadInvoiceBlock}
            {shareInvoiceBlock}
          </Space>
        );
      } else {
        return (
          <Space direction="vertical">
            {commonBtnNode(
              false,
              t('workspace.list.markAsPaid'),
              SHIPPING_BILLING
            )}
            {invoice.markAsPaidTime && (
              <div className="date-wrap" style={{ textAlign: 'center' }}>
                <TextHeavy>{t('workspace.list.paymentSentOn')}:</TextHeavy>
                <div>
                  <RevxDate
                    dateStr={currentJob.invoice.markAsPaidTime}
                    formatStr={SHIPPING_DATE_FORMAT}
                  />
                </div>
              </div>
            )}
            {generateInvoiceBtnBlock}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '1px',
                flexWrap: 'wrap', // 在小屏幕上允许换行
              }}
            >
              {sendInvoiceFileBlock}
              {sendQuickBooksInvoiceFileBlock}
            </div>
            {batchUploadInvoiceBlock}
            {shareInvoiceBlock}
          </Space>
        );
      }
    } else {
      return (
        <Space direction="vertical">
          <h3 style={{ fontWeight: 'bold' }}>
            {t('workspace.list.jobCompleted')}
          </h3>
          {generateInvoiceBtnBlock}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '1px',
              flexWrap: 'wrap', // 在小屏幕上允许换行
            }}
          >
            {sendInvoiceFileBlock}
            {sendQuickBooksInvoiceFileBlock}
          </div>
          {batchUploadInvoiceBlock}
          {receiveInvoiceFileBlock}
          {receiveQuickBooksInvoiceFileBlock}
        </Space>
      );
    }
  };

  const completeNode = () => {
    // console.log('hasProducer: ', hasProducer, 'hasOperator:', hasOperator);
    if (hasOperator) {
      return paymentProgressBlock();
    } else if (hasProducer) {
      return (
        <Space direction="vertical" style={{ textAlign: 'center' }}>
          {!showInvoiceButton() &&
            commonBtnNode(true, t('workspace.list.markAsPaid'))}
          <div className="date-wrap">
            <TextHeavy>{t('workspace.list.dateMarkedAsPaid')}: </TextHeavy>
            <RevxDate
              dateStr={currentJob.updateAt}
              formatStr={SHIPPING_DATE_FORMAT}
            />
          </div>
          {generateInvoiceBtnBlock}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '1px',
              flexWrap: 'wrap', // 在小屏幕上允许换行
            }}
          >
            {sendInvoiceFileBlock}
            {sendQuickBooksInvoiceFileBlock}
          </div>
          {batchUploadInvoiceBlock}
          {shareInvoiceBlock}
        </Space>
      );
    } else {
      return (
        <h3 style={{ fontWeight: 'bold' }}>
          {t('workspace.list.jobCompleted')}
        </h3>
      );
    }
  };

  const shareInvoiceBlock =
    showInvoiceButton() &&
    (sendInvoiceFileUrl || sendQuickBooksInvoiceFileUrl) ? (
      <div style={{ textAlign: 'center' }}>
        <Space>
          <ShareInvoiceDropdown
            jobItem={currentJob}
            onUpdate={handleShareInvoiceSuccess}
          >
            <RevxFlexLayout
              className="share-quote-wrap"
              style={{
                alignItems: 'flex-start',
                width: '100%',
                height: 'auto',
                minHeight: 'fit-content',
                flexWrap: 'nowrap',
                flexShrink: 0,
                justifyContent: 'space-between',
                gap: '8px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: '8px',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {currentJob?.shareList?.length && (
                  <span
                    className="label"
                    style={{
                      whiteSpace: 'nowrap',
                      alignSelf: 'flex-start',
                      marginTop: '2px',
                      marginRight: '1px',
                    }}
                  >
                    Shared With
                  </span>
                )}
                <div
                  className="name-badge-wrap"
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1px', // 同时设置水平和垂直间距
                    alignItems: 'flex-start',
                    flex: 1,
                  }}
                >
                  {currentJob?.shareList?.map((i: any, k: number) => (
                    <div
                      key={k}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <NameBadge name={`${i.firstName} ${i.lastName}`} />
                      {i.expired && (
                        <Tooltip title="Job has been adjusted, please regenerate and share">
                          <ExclamationCircleOutlined
                            style={{
                              color: '#ff4d4f',
                              fontSize: '14px',
                              cursor: 'help',
                            }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <Button
                className="share"
                shape="round"
                onClick={() => {}}
                style={{ flexShrink: 0 }}
              >
                <PlusOutlined />
                Share
              </Button>
            </RevxFlexLayout>
          </ShareInvoiceDropdown>
        </Space>
      </div>
    ) : null;

  // const shareInvoiceBlock2 = sendInvoiceFileUrl ? (
  //   <div style={{ textAlign: 'center' }}>
  //     <Space>
  //       <ShareInvoice jobItem={data}>
  //         <UnderlineText
  //           color="blue"
  //           onClick={() => {}}
  //         >
  //           {t('workspace.list.shareQuote')}
  //         </UnderlineText>
  //       </ShareInvoice>
  //     </Space>
  //   </div>
  // ) : null;

  const jobAdjustmentNode = () => {
    if (hasOperator) {
      return (
        <>
          <RevxFlexLayout justify="justify-center">
            <RevxFlexLayout
              justify="justify-center"
              style={{ marginBottom: '12px' }}
            >
              {viewJobAdjustmentBtn}
            </RevxFlexLayout>
          </RevxFlexLayout>
          <CardActionArchiveBtn data={currentJob} />
        </>
      );
    } else if (hasProducer) {
      if (isInternalJob) {
        return (
          <>
            <RevxFlexLayout justify="justify-center">
              <div style={{ width: '304px', marginBottom: '12px' }}>
                {viewJobAdjustmentBtn}
              </div>
            </RevxFlexLayout>
            <CardActionArchiveBtn data={currentJob} />
          </>
        );
      } else {
        return (
          <>
            <RevxFlexLayout justify="justify-center">
              <div style={{ width: '304px', marginBottom: '12px' }}>
                {viewJobAdjustmentBtn}
              </div>
            </RevxFlexLayout>
            <CardActionArchiveBtn data={currentJob} />
          </>
        );
      }
    } else {
      return (
        <>
          <RevxFlexLayout
            justify="justify-center"
            style={{ marginBottom: '12px' }}
          >
            {viewJobAdjustmentBtn}
          </RevxFlexLayout>
          <CardActionArchiveBtn data={currentJob} />
        </>
      );
    }
  };

  const workflowActions = () => {
    switch (workflowStatus) {
      case PO_SENT:
      case PO_READ:
      case PO_ACCEPT:
      case RFM:
        return (
          <>
            {attachNode()}
            {poNode()}
          </>
        );
      case PREPARE_PROOF:
      case PROOF_AWAIT_APPROVAL:
      case PROOF_NEED_REVISION:
        return proofNode();
      case IN_PRODUCTION:
        return inProductionNode();
      case SHIPPED:
        return <>{shippingNode()}</>;
      case DELIVERED:
        return <>{deliverNode()}</>;
      case INVOICING:
        return invoiceNode();
      case COMPLETED:
        return completeNode();
    }
  };

  return (
    <div className="workspace-item-actions">
      <div
        className="workspace-item-right"
        style={{
          minHeight: '100px', // 增加最小高度
          display: 'flex',
          alignItems: 'center', // 内容向上对齐
          justifyContent: 'center',
          minWidth: '320px',
          padding: '8px 8px', // 设置最小宽度，防止内容过于紧凑
          height: 'auto',
          gap: '10px',
        }}
      >
        {workflowActions()}
      </div>
      <div className="workspace-item-extra-slot">
        {jobAdjustmentNode()}
        <CardCancelJobBtn data={currentJob} />
        <CardBuyerCancelJobBtn data={currentJob} />
      </div>
    </div>
  );
};

export default CardActions;
